import { axiosPrivate } from 'shared/api';
import toast from 'shared/utils/toast';
import { IDocument } from 'features/inventory/documents-G4/types';
import { BaseService } from 'shared/services/BaseService';

class FetchDocumentService extends BaseService<IDocument> {
  G4L_file = 'documentsG4File';

  constructor() {
    super('documentsLM');
    this.canDelete = true;
  }

  protected appendObjectsToFormData(formData: FormData, objects_id: any[], fieldName: string) {
    objects_id.forEach((item) => {
      formData.append(fieldName + 's', item.toString());
    });
  }

  uploadDocument = async (
    tipology: any,
    attachment: File,
    name: string,
    description: string,
    folder: string,
    folder_description: string,
    category?: string,
    supplier?: string,
    warehouse?: string,

    fieldName = '',
    objects_id?: any[]
  ) => {
    const formData = new FormData();
    formData.append('tipology', typeof tipology === 'string' ? tipology : tipology.name);
    formData.append('attachment', attachment);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('supplier', supplier ?? '');
    formData.append('warehouse', warehouse ?? '');
    formData.append('folder', '/' + folder);
    formData.append('folder_description', folder_description ?? '');

    if (objects_id && objects_id.length > 0 && fieldName) {
      this.appendObjectsToFormData(formData, objects_id, fieldName);
    }

    const response = await axiosPrivate.post<IDocument>(
      `${this.modulePrefix}/${fieldName}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  };

  uploadMultipleDocuments = async (files: File[]) => {
    const promises = files.map((file) => this.uploadSingleDocument(file));

    return Promise.allSettled(promises);
  };

  uploadSingleDocument = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', file.webkitRelativePath || file.name);

    const response = await axiosPrivate.post<{ task_id: string; file: string }>(
      `${this.modulePrefix}/upload-single/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  };

  getUploadStatus = async (taskId: string) => {
    try {
      const response = await axiosPrivate.get(`${this.modulePrefix}/upload-status/${taskId}/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.message || 'Errore durante il recupero dello stato upload');
    }
  };

  /**
   * Scarica l'allegato del documento LM tramite API tenant (AttachmentMixin).
   * (Nome distinto da BaseService.downloadAttachment entityId/attachmentName.)
   * @param documentSubtype es. supplier_archive, equipment (omit per documenti root DocumentLM)
   * @param entityId id del documento
   */
  downloadLmAttachment = async (documentSubtype: string | undefined, entityId: number) => {
    try {
      const path =
        documentSubtype != null && documentSubtype !== ''
          ? `${this.modulePrefix}/${documentSubtype}/${entityId}/download-attachment/attachment/`
          : `${this.modulePrefix}/${entityId}/download-attachment/attachment/`;
      const res = await axiosPrivate.get(path, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const contentDisposition = res.headers['content-disposition'];
      let filename = 'document';
      if (contentDisposition?.includes('attachment')) {
        const filenameMatches = contentDisposition.match(
          /(?:.*filename\*|filename)=(?:([^'"]*)''|("))([^;]+)\2(?:[;`\n]|$)/
        );
        if (filenameMatches?.length) {
          filename = filenameMatches.pop() ?? filename;
        }
      }
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? e?.message ?? 'Download non riuscito');
    }
  };

  getAllPaginatedDocuments = async (
    documentType = '',
    limit = 0,
    offset = 0,
    params: Record<string, unknown> = {}
  ) => {
    try {
      const url = `${this.modulePrefix}/${documentType}/`.replace(/\/?$/, '/');
      const res = await axiosPrivate.get(url, {
        params: { limit, offset, ...params }
      });
      return res.data;
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  getAdvancedSearchDocuments = async (
    limit = 0,
    offset = 0,
    params: Record<string, unknown> = {}
  ) => {
    try {
      const url = `${this.modulePrefix}/advanced-db-search/`.replace(/\/?$/, '/');
      const res = await axiosPrivate.get(url, {
        params: { limit, offset, ...params }
      });
      return res.data;
    } catch (e: any) {
      toast.error(e.message);
    }
  };
}

export const fmDocumentService = new FetchDocumentService();
